import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2, Calendar, Clock, Tag, Users } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { taskService } from '@/services/taskService'
import { matchingService } from '@/services/matchingService'
import { useToast } from '@/contexts/ToastContext'
import {
  Task,
  TaskType,
  TaskPriority,
  CreateTaskRequest,
  ResourceType
} from '@/types/task'
import { Relationship } from '@/types/matching'

interface CreateTaskModalProps {
  isOpen: boolean
  onClose: () => void
  onTaskCreated: (task: Task) => void
  preselectedRelationshipId?: string
  preselectedMenteeId?: string
}

const TASK_TYPES: { value: TaskType; label: string }[] = [
  { value: 'SKILL_DEVELOPMENT', label: 'Skill Development' },
  { value: 'PROJECT_WORK', label: 'Project Work' },
  { value: 'CAREER_GUIDANCE', label: 'Career Guidance' },
  { value: 'RESEARCH', label: 'Research' },
  { value: 'READING', label: 'Reading' },
  { value: 'PRACTICE', label: 'Practice' },
  { value: 'OTHER', label: 'Other' }
]

const PRIORITIES: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' }
]

interface MilestoneInput {
  title: string
  description?: string
  dueDate?: string
}

interface ResourceInput {
  type: ResourceType
  title: string
  url?: string
  description?: string
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  isOpen,
  onClose,
  onTaskCreated,
  preselectedRelationshipId,
  preselectedMenteeId
}) => {
  const { showToast } = useToast()

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<TaskType>('SKILL_DEVELOPMENT')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [dueDate, setDueDate] = useState('')
  const [estimatedHours, setEstimatedHours] = useState<number | ''>('')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [milestones, setMilestones] = useState<MilestoneInput[]>([])
  const [resources, setResources] = useState<ResourceInput[]>([])

  // Mentee selection state
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [selectedMenteeIds, setSelectedMenteeIds] = useState<string[]>([])
  const [loadingRelationships, setLoadingRelationships] = useState(false)

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load relationships when modal opens
  useEffect(() => {
    if (isOpen) {
      loadRelationships()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Set preselected mentee if provided
  useEffect(() => {
    if (preselectedMenteeId && preselectedRelationshipId) {
      setSelectedMenteeIds([preselectedMenteeId])
    }
  }, [preselectedMenteeId, preselectedRelationshipId])

  const loadRelationships = async () => {
    try {
      setLoadingRelationships(true)
      const data = await matchingService.getMyRelationships()
      // Filter to only active relationships
      const activeRelationships = data.filter(r => r.status === 'active')
      setRelationships(activeRelationships)
    } catch (error) {
      showToast({
        type: 'error',
        message: 'Failed to load mentees'
      })
    } finally {
      setLoadingRelationships(false)
    }
  }

  const handleMenteeToggle = (menteeId: string) => {
    setSelectedMenteeIds(prev =>
      prev.includes(menteeId)
        ? prev.filter(id => id !== menteeId)
        : [...prev, menteeId]
    )
  }

  const handleSelectAllMentees = () => {
    if (selectedMenteeIds.length === relationships.length) {
      setSelectedMenteeIds([])
    } else {
      setSelectedMenteeIds(relationships.map(r => r.menteeId))
    }
  }

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim().toLowerCase()
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 10) {
      setTags([...tags, trimmedTag])
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag))
  }

  const handleAddMilestone = () => {
    if (milestones.length < 10) {
      setMilestones([...milestones, { title: '', description: '', dueDate: '' }])
    }
  }

  const handleUpdateMilestone = (index: number, field: keyof MilestoneInput, value: string) => {
    const updated = [...milestones]
    updated[index] = { ...updated[index], [field]: value }
    setMilestones(updated)
  }

  const handleRemoveMilestone = (index: number) => {
    setMilestones(milestones.filter((_, i) => i !== index))
  }

  const handleAddResource = () => {
    if (resources.length < 10) {
      setResources([...resources, { type: 'link', title: '', url: '', description: '' }])
    }
  }

  const handleUpdateResource = (index: number, field: keyof ResourceInput, value: string) => {
    const updated = [...resources]
    updated[index] = { ...updated[index], [field]: value }
    setResources(updated)
  }

  const handleRemoveResource = (index: number) => {
    setResources(resources.filter((_, i) => i !== index))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!title.trim()) {
      newErrors.title = 'Title is required'
    } else if (title.length < 3) {
      newErrors.title = 'Title must be at least 3 characters'
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required'
    } else if (description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters'
    }

    if (selectedMenteeIds.length === 0) {
      newErrors.mentees = 'Select at least one mentee'
    }

    // Validate milestones have titles
    milestones.forEach((m, i) => {
      if (m.title && m.title.length < 3) {
        newErrors[`milestone_${i}`] = 'Milestone title must be at least 3 characters'
      }
    })

    // Validate resources have titles and URLs
    resources.forEach((r, i) => {
      if (r.title && !r.url && r.type !== 'book') {
        newErrors[`resource_${i}`] = 'Resource URL is required'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const milestonesData = milestones
        .filter(m => m.title.trim())
        .map(m => ({
          title: m.title,
          description: m.description || undefined,
          dueDate: m.dueDate || undefined
        }))
      const resourcesData = resources
        .filter(r => r.title.trim())
        .map(r => ({
          type: r.type,
          title: r.title,
          url: r.url || undefined,
          description: r.description || undefined
        }))

      let createdTasks: Task[]

      if (selectedMenteeIds.length > 1) {
        createdTasks = await taskService.createGroupTask({
          menteeIds: selectedMenteeIds,
          title,
          description,
          type,
          priority,
          dueDate: dueDate || undefined,
          estimatedHours: estimatedHours || undefined,
          tags: tags.length > 0 ? tags : undefined,
          milestones: milestonesData,
          resources: resourcesData
        })
      } else {
        const menteeId = selectedMenteeIds[0]
        const relationship = relationships.find(r => r.menteeId === menteeId)

        const taskData: CreateTaskRequest = {
          relationshipId: relationship!.id,
          menteeId,
          title,
          description,
          type,
          priority,
          dueDate: dueDate || undefined,
          estimatedHours: estimatedHours || undefined,
          tags: tags.length > 0 ? tags : undefined,
          milestones: milestonesData,
          resources: resourcesData
        }

        createdTasks = [await taskService.createTask(taskData)]
      }

      showToast({
        type: 'success',
        message: createdTasks.length > 1
          ? `Created ${createdTasks.length} tasks successfully`
          : 'Task created successfully'
      })

      onTaskCreated(createdTasks[0])

      handleClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create task'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset form
      setTitle('')
      setDescription('')
      setType('SKILL_DEVELOPMENT')
      setPriority('medium')
      setDueDate('')
      setEstimatedHours('')
      setTags([])
      setTagInput('')
      setMilestones([])
      setResources([])
      setSelectedMenteeIds([])
      setErrors({})
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create New Task</h2>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* Mentee Selection */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Users className="w-4 h-4 inline mr-1" />
                  Assign to Mentee(s) *
                </label>
                {relationships.length > 1 && (
                  <button
                    type="button"
                    onClick={handleSelectAllMentees}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {selectedMenteeIds.length === relationships.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>

              {loadingRelationships ? (
                <div className="text-sm text-gray-500">Loading mentees...</div>
              ) : relationships.length === 0 ? (
                <div className="text-sm text-gray-500">No active mentees found</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {relationships.map((relationship) => (
                    <label
                      key={relationship.id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedMenteeIds.includes(relationship.menteeId)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedMenteeIds.includes(relationship.menteeId)}
                        onChange={() => handleMenteeToggle(relationship.menteeId)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {relationship.mentee?.firstName} {relationship.mentee?.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {relationship.mentee?.email}
                        </p>
                      </div>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedMenteeIds.includes(relationship.menteeId)
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedMenteeIds.includes(relationship.menteeId) && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                            <path d="M10.28 2.28L4 8.56 1.72 6.28a.75.75 0 00-1.06 1.06l3 3a.75.75 0 001.06 0l7-7a.75.75 0 00-1.06-1.06z" />
                          </svg>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {errors.mentees && (
                <p className="text-sm text-red-500 mt-1">{errors.mentees}</p>
              )}
              {selectedMenteeIds.length > 1 && (
                <p className="text-sm text-accent-700 mt-2">
                  This will create {selectedMenteeIds.length} separate tasks (one per mentee)
                </p>
              )}
            </div>

            {/* Title */}
            <div className="mb-4">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter task title"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
                maxLength={200}
              />
              {errors.title && (
                <p className="text-sm text-red-500 mt-1">{errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div className="mb-4">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the task objectives and requirements..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                rows={4}
                disabled={isSubmitting}
                maxLength={2000}
              />
              {errors.description && (
                <p className="text-sm text-red-500 mt-1">{errors.description}</p>
              )}
            </div>

            {/* Type and Priority */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                  Task Type
                </label>
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value as TaskType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={isSubmitting}
                >
                  {TASK_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  id="priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={isSubmitting}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Due Date and Estimated Hours */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Due Date
                </label>
                <input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Estimated Hours
                </label>
                <input
                  id="estimatedHours"
                  type="number"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(e.target.value ? parseInt(e.target.value) : '')}
                  min="0"
                  max="1000"
                  placeholder="e.g., 10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Tags */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Tag className="w-4 h-4 inline mr-1" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add a tag"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={isSubmitting || tags.length >= 10}
                  maxLength={50}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddTag}
                  disabled={isSubmitting || !tagInput.trim() || tags.length >= 10}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Milestones (Collapsible) */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Milestones (Optional)
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleAddMilestone}
                  disabled={isSubmitting || milestones.length >= 10}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Milestone
                </Button>
              </div>
              {milestones.map((milestone, index) => (
                <Card key={index} className="mb-2">
                  <CardBody className="p-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={milestone.title}
                          onChange={(e) => handleUpdateMilestone(index, 'title', e.target.value)}
                          placeholder="Milestone title"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={milestone.description || ''}
                            onChange={(e) => handleUpdateMilestone(index, 'description', e.target.value)}
                            placeholder="Description (optional)"
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                          <input
                            type="date"
                            value={milestone.dueDate || ''}
                            onChange={(e) => handleUpdateMilestone(index, 'dueDate', e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveMilestone(index)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>

            {/* Resources (Collapsible) */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Resources (Optional)
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleAddResource}
                  disabled={isSubmitting || resources.length >= 10}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Resource
                </Button>
              </div>
              {resources.map((resource, index) => (
                <Card key={index} className="mb-2">
                  <CardBody className="p-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <select
                            value={resource.type}
                            onChange={(e) => handleUpdateResource(index, 'type', e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="link">Link</option>
                            <option value="document">Document</option>
                            <option value="video">Video</option>
                            <option value="book">Book</option>
                            <option value="course">Course</option>
                            <option value="other">Other</option>
                          </select>
                          <input
                            type="text"
                            value={resource.title}
                            onChange={(e) => handleUpdateResource(index, 'title', e.target.value)}
                            placeholder="Resource title"
                            className="col-span-2 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <input
                          type="url"
                          value={resource.url || ''}
                          onChange={(e) => handleUpdateResource(index, 'url', e.target.value)}
                          placeholder="URL (https://...)"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveResource(index)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end border-t pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || selectedMenteeIds.length === 0}
                isLoading={isSubmitting}
              >
                {selectedMenteeIds.length > 1
                  ? `Create ${selectedMenteeIds.length} Tasks`
                  : 'Create Task'
                }
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateTaskModal
